import { useState } from 'react'
import * as S from '../EmbeddedLogin.styles'
import { useAuth } from 'services/AuthProvider/AuthProvider'
import { OTP_SESSION_EXPIRED_ERROR } from 'services/AuthProvider/cognito'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { extractApiError } from 'utils/ApiUtils'
import { translateManager as t } from '../../../services/TranslateManager'
import UserManager, {
  CAPTURE_EMAIL_SOURCE,
} from '../../../services/UserManager'
import {
  loginReport,
  SHOW_LOGIN_SNACKBAR_KEY,
} from '../../../services/LoginUtills'
import SessionStorageManager from '../../../services/SessionStorageManager'
import {
  markLoginReloadAbandoned,
  markLoginReloadPending,
} from 'features/bookArrivalNotice/loginNotice'
import { useResendCodeCooldown } from './useResendCodeCooldown'

interface EmbeddedLoginCodeDialogProps {
  showError: (error: any) => void
  setShowCode: (value: boolean) => void
  email: string
  onLoginProcessStart?: () => void
  skipRefresh?: boolean
}

function EmbeddedLoginCodeDialog({
  showError,
  setShowCode,
  email,
  onLoginProcessStart,
  skipRefresh = false,
}: EmbeddedLoginCodeDialogProps) {
  const [code, setCode] = useState('')
  const [isLoading, setLoading] = useState(false)
  const [isResendingCode, setIsResendingCode] = useState(false)
  const { secondsRemaining, restartCooldown } = useResendCodeCooldown()
  const { confirmEmailCode, getIdToken, initiateEmailSignIn } = useAuth()
  const isResendDisabled = secondsRemaining > 0 || isResendingCode

  const handleCodeChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setCode(value)
  }

  const resendCode = async () => {
    if (isResendDisabled) {
      return
    }

    setIsResendingCode(true)
    try {
      analytics.track(EVENT_NAMES.LOGIN_RESEND_CODE_CLICKED, { email })
      await initiateEmailSignIn(email)
      restartCooldown()
      setShowCode(true)
    } catch (e) {
      const error = extractApiError(e) || e
      showError(error)
    } finally {
      setIsResendingCode(false)
    }
  }

  const handleCodeSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    setLoading(true)
    analytics.track(EVENT_NAMES.LOGIN_EMAIL_CODE_ENTERED)
    e.preventDefault()
    // Claimed before the awaits below, not beside the reload: signing in merges
    // the account's books partway through, and whatever that merge has to
    // announce belongs to the document this one is about to be replaced by.
    if (!skipRefresh) {
      markLoginReloadPending()
    }
    try {
      await confirmEmailCode({ email }, code)
      await loginReport({
        token: await getIdToken(),
        email,
        isEmbeddedLogin: true,
      })
      onLoginProcessStart?.()
      await UserManager.setUserEmail({
        email,
        source: CAPTURE_EMAIL_SOURCE.login,
        refetchExperiments: true,
        onboardingFlow: undefined,
        emailConsent: UserManager.hasExplicitEmailConsent(email),
      })
      if (!skipRefresh) {
        SessionStorageManager.set(SHOW_LOGIN_SNACKBAR_KEY, true)
        window.location.reload()
      }
    } catch (e) {
      markLoginReloadAbandoned()
      setCode('')
      const error = extractApiError(e) || e
      showError(error)
      if (e instanceof Error && e.name === OTP_SESSION_EXPIRED_ERROR) {
        setShowCode(false)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <S.Content>
      <S.CodeTitle>{t.get('login.email_code.code_sent')}</S.CodeTitle>
      <S.CodeSubtitle>
        {t.get('login.email_code.code_sent_detailed', { email })}
      </S.CodeSubtitle>
      <S.CodeForm onSubmit={handleCodeSubmit}>
        <S.LoginInput
          data-testid="embedded-login-code-input"
          autoFocus
          type="number"
          inputMode="numeric"
          placeholder={t.get('login.email_code.enter_code')}
          value={code}
          required
          onChange={handleCodeChanged}
        />
        <S.Continue
          data-testid="embedded-login-code-submit-button"
          type="submit"
          loading={isLoading}
          disabled={isLoading}
        >
          {t.get('new_email_capture.continue')}
        </S.Continue>
      </S.CodeForm>
      <S.ResendCodeButton
        data-testid="embedded-login-resend-code-button"
        type="button"
        disabled={isResendDisabled}
        onClick={resendCode}
      >
        {secondsRemaining > 0
          ? t.get('login.email_code.resend_code_countdown', {
              seconds: secondsRemaining,
            })
          : t.get('login.email_code.resend_code')}
      </S.ResendCodeButton>
    </S.Content>
  )
}

export default EmbeddedLoginCodeDialog
